import { useState } from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight, Phone, Building2, Loader2, CheckCircle2, ShieldCheck, Clock, FileText, Users } from "lucide-react";
import { Seo } from "../../components/public/Seo";
import { TiltCard } from "../../components/public/TiltCard";
import { MagneticButton } from "../../components/public/MagneticButton";
import { api, COMPANY, useSiteContent } from "../../lib/api";
import { AddressInput } from "../../components/public/AddressInput";

const WHY = [
  { icon: Users, title: "Single point of contact", text: "One number, one team — no runaround across your whole portfolio." },
  { icon: FileText, title: "Fast written quotes", text: "Clear, documented quotes so you can approve and report with confidence." },
  { icon: ShieldCheck, title: "Licensed, insured & compliant", text: "Fully licensed and insured, OH&S compliant with SWMS on every site." },
  { icon: Clock, title: "Tenant-friendly practices", text: "Tidy, low-disruption site work and clear reporting after every visit." },
];

function PmForm({ ctaText }) {
  const [f, setF] = useState({ name: "", company: "", portfolio_size: "", phone: "", email: "", address: "", message: "" });
  const [status, setStatus] = useState("idle");
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.phone || !f.address) return;
    setStatus("loading");
    try {
      await api.post("/leads", {
        name: f.name, phone: f.phone, email: f.email, company: f.company,
        portfolio_size: f.portfolio_size, message: f.message, address: f.address,
        job_type: "PM/Strata portfolio", suburb: "", source: "pm_strata",
        lead_type: "property-manager/strata",
      });
      setStatus("done");
    } catch { setStatus("error"); }
  };
  const cls = "w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-[#1A2421] placeholder:text-[#9aa39e] focus:outline-none focus:ring-2 focus:ring-[#62B848] transition";

  if (status === "done") return (
    <div data-testid="pm-success" className="bg-white rounded-3xl border border-[#62B848]/30 p-8 text-center shadow-xl">
      <div className="grid place-items-center w-16 h-16 rounded-2xl bg-[#62B848]/15 text-[#62B848] mx-auto mb-5"><CheckCircle2 size={34} /></div>
      <h3 className="font-display font-extrabold text-2xl text-[#1A2421] mb-2">Thanks, {f.name.split(" ")[0]}!</h3>
      <p className="text-[#4A5550]">We'll be in touch to arrange your portfolio roof report. For anything urgent, call {COMPANY.phone}.</p>
    </div>
  );

  return (
    <form onSubmit={submit} data-testid="pm-form" className="bg-white rounded-3xl border border-black/5 shadow-xl p-7 md:p-9">
      <h3 className="font-display font-extrabold text-2xl text-[#1A2421]">{ctaText}</h3>
      <p className="text-[#4A5550] text-sm mt-1 mb-6">Portfolio roof reports for property managers &amp; strata.</p>
      <div className="grid gap-3.5 sm:grid-cols-2">
        <input data-testid="pm-name" className={cls} placeholder="Your name *" value={f.name} onChange={set("name")} required />
        <input data-testid="pm-company" className={cls} placeholder="Company" value={f.company} onChange={set("company")} />
        <input data-testid="pm-portfolio" className={cls} placeholder="Portfolio size (e.g. 40 lots)" value={f.portfolio_size} onChange={set("portfolio_size")} />
        <input data-testid="pm-phone" className={cls} placeholder="Phone *" value={f.phone} onChange={set("phone")} required />
        <input data-testid="pm-email" className={`${cls} sm:col-span-2`} placeholder="Email" value={f.email} onChange={set("email")} />
        <AddressInput testId="pm-address" className={`${cls} sm:col-span-2`} required value={f.address} onChange={(v) => setF((s) => ({ ...s, address: v }))} placeholder="Property / site address *" />
      </div>
      <textarea data-testid="pm-message" className={`${cls} mt-3.5 resize-none`} rows={3} placeholder="Tell us about your portfolio (optional)" value={f.message} onChange={set("message")} />
      {status === "error" && <p className="text-red-600 text-sm mt-3">Something went wrong — please call {COMPANY.phone}.</p>}
      <MagneticButton type="submit" data-testid="pm-submit" disabled={status === "loading"}
        className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#62B848] hover:bg-[#4E9B38] disabled:opacity-60 text-white font-bold px-6 py-3.5 rounded-full transition-colors">
        {status === "loading" ? <><Loader2 size={18} className="animate-spin" /> Sending…</> : ctaText}
      </MagneticButton>
    </form>
  );
}

export default function PropertyStrata() {
  const content = useSiteContent();
  const pm = content.pm_strata_page || {};
  const offers = (pm.offer_items && pm.offer_items.length) ? pm.offer_items : [
    "Portfolio roof reports & condition assessments",
    "Preventative maintenance plans",
    "Leak response with priority scheduling",
    "End-of-lease & insurance makegoods",
    "Storm damage & insurance claim support with full photo documentation",
    "Large-scale commercial remedial works",
  ];
  const cta = pm.cta_text || "Book a portfolio roof report";

  return (
    <>
      <Seo title="Property & Strata Managers" description="West Face Roofing is Perth's roofing partner for property managers, strata companies and commercial portfolios — roof reports, maintenance plans, priority leak response and remedial works." path="/property-strata-managers" />

      <section className="pt-32 md:pt-44 pb-16 bg-[#224733] text-white relative overflow-hidden wf-grain">
        <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-[#62B848]/15 blur-3xl" />
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10">
          <span className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-4 py-2 text-sm font-semibold mb-5"><Building2 size={15} className="text-[#62B848]" /> Property &amp; Strata Managers</span>
          <h1 className="font-display font-black heading-up uppercase tracking-tight text-4xl sm:text-6xl lg:text-7xl leading-[0.95] max-w-4xl text-balance">
            {pm.headline || "Your roofing partner for Perth's property & strata portfolios."}
          </h1>
          <p className="mt-6 text-white/70 text-lg max-w-xl leading-relaxed">Roofing partner for property managers, strata companies and commercial portfolios across Perth. Fast quotes, clear reporting, no fuss.</p>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-[#F8F9FA]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-[1.4fr_1fr] gap-12">
          <div>
            <p className="text-[#62B848] font-bold tracking-wide uppercase text-sm mb-3">What we offer</p>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-[#1A2421] leading-tight mb-8">Built for portfolios, not just single jobs.</h2>
            <div className="grid sm:grid-cols-2 gap-4" style={{ perspective: 1200 }}>
              {offers.map((o, i) => (
                <motion.div key={o} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
                  <TiltCard className="h-full" max={7}>
                    <div className="flex items-start gap-3 bg-white rounded-2xl border border-black/5 p-5 h-full">
                      <span className="grid place-items-center w-8 h-8 rounded-xl bg-[#62B848]/15 text-[#62B848] shrink-0"><Check size={16} /></span>
                      <span className="text-[#1A2421] font-medium">{o}</span>
                    </div>
                  </TiltCard>
                </motion.div>
              ))}
            </div>

            <p className="text-[#62B848] font-bold tracking-wide uppercase text-sm mb-3 mt-12">Why PMs choose us</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {WHY.map((w) => (
                <div key={w.title} className="bg-white rounded-2xl border border-black/5 p-6">
                  <div className="grid place-items-center w-11 h-11 rounded-xl bg-[#224733]/8 text-[#224733] mb-3"><w.icon size={20} /></div>
                  <h3 className="font-display font-bold text-[#1A2421] mb-1">{w.title}</h3>
                  <p className="text-[#4A5550] text-sm leading-relaxed">{w.text}</p>
                </div>
              ))}
            </div>
            <a href={COMPANY.phoneHref} className="mt-8 inline-flex items-center gap-2 bg-[#224733] hover:bg-[#1A3626] text-white font-bold px-7 py-3.5 rounded-full transition-colors"><Phone size={18} /> {COMPANY.phone}</a>
          </div>

          <div className="lg:sticky lg:top-28 self-start"><PmForm ctaText={cta} /></div>
        </div>
      </section>
    </>
  );
}
